<template>
  <Teleport :to="portalRootElementSelector">
    <ModalRoot
      v-model="isOpen"
      :keep-tree-alive="true"
      aria-labelledby="offer-detail-modal-title"
      :aria-label="
        props.offer
          ? $t('channel.offers.modal.titleWithName', { name: props.offer.name })
          : $t('channel.offers.modal.title')
      "
      @close="onClose"
    >
      <template #body="{ close }">
        <ModalBodyV2
          class="!bg-fill-brand-secondary !backdrop-blur-[50px] !max-w-[497px] !px-7 !gap-6"
          @close="close"
        >
          <template #title>
            <h2
              id="offer-detail-modal-title"
              class="font-bold text-xl text-text-primary leading-normal h-full flex items-center"
            >
              {{ $t("channel.offers.modal.title") }}
            </h2>
          </template>

          <div v-if="props.offer" class="flex flex-col gap-5">
            <!-- Offer image -->
            <div
              class="w-full aspect-video max-h-80 rounded-2xl overflow-hidden bg-surface-secondary"
            >
              <img
                v-if="props.offer.image_url"
                :src="props.offer.image_url"
                :alt="props.offer.name"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center"
              >
                <Icon
                  icon="mdi:tag-outline"
                  class="text-icon-secondary text-5xl"
                />
              </div>
            </div>

            <!-- Offer name -->
            <h3 class="font-semibold text-xl text-text-primary">
              {{ props.offer.name }}
            </h3>

            <!-- Description -->
            <p
              v-if="props.offer.description"
              class="font-medium text-base text-text-secondary leading-6 whitespace-pre-wrap"
            >
              {{ props.offer.description }}
            </p>

            <!-- Offer URL (displays for offers with a url value) -->
            <p
              v-if="props.offer.url"
              class="text-sm text-text-secondary line-clamp-1 truncate"
            >
              {{ props.offer.url }}
            </p>

            <!-- Validity dates -->
            <div
              v-if="props.offer.valid_from || props.offer.valid_until"
              class="flex flex-col gap-1 text-sm text-text-secondary"
            >
              <span v-if="props.offer.valid_from">
                {{
                  $t("channel.offers.modal.validFrom", {
                    date: formatDate(props.offer.valid_from),
                  })
                }}
              </span>
              <span v-if="props.offer.valid_until">
                {{
                  $t("channel.offers.modal.validUntil", {
                    date: formatDate(props.offer.valid_until),
                  })
                }}
              </span>
            </div>

            <!-- Sold out state -->
            <div
              v-if="isSoldOut"
              class="flex items-center gap-2 bg-surface-secondary rounded-xl px-4 py-3 border border-border-brand"
            >
              <Icon
                icon="mdi:tag-off-outline"
                class="text-icon-secondary text-xl shrink-0"
              />
              <span class="text-text-secondary text-base font-medium">
                {{ $t("channel.offers.modal.soldOut") }}
              </span>
            </div>

            <!-- Claimed coupon display (value known from this session) -->
            <div v-else-if="claimedCoupon" class="flex flex-col gap-3">
              <!-- Code type -->
              <template
                v-if="props.offer.offer_coupon_type === OfferCouponType.Code"
              >
                <p
                  class="text-sm text-text-secondary font-medium uppercase tracking-wide"
                >
                  {{ $t("channel.offers.modal.yourCouponCode") }}
                </p>
                <div
                  class="relative flex items-center bg-surface-secondary border border-border-brand rounded-xl pl-4 pr-1 py-1"
                >
                  <code
                    class="flex-1 font-mono text-base text-text-primary select-all break-all py-2"
                  >
                    {{ couponValue }}
                  </code>
                  <ButtonV2
                    v-if="couponValue"
                    variant="primary"
                    size="sm"
                    class="shrink-0 ml-2"
                    @click="onCopyCode"
                  >
                    <Icon icon="mdi:content-copy" class="text-sm" />
                    {{ $t("channel.offers.modal.copy") }}
                  </ButtonV2>
                </div>
                <div
                  class="flex items-center gap-2 rounded-lg bg-surface-secondary px-3 py-2"
                >
                  <Icon
                    icon="mdi:information-outline"
                    class="text-icon-secondary text-base shrink-0"
                  />
                  <span class="text-sm text-text-secondary">
                    {{ $t("channel.offers.modal.singleUseNote") }}
                  </span>
                </div>
              </template>

              <!-- Link type -->
              <template
                v-else-if="
                  props.offer.offer_coupon_type === OfferCouponType.Link
                "
              >
                <p
                  class="text-sm text-text-secondary font-medium uppercase tracking-wide"
                >
                  {{ $t("channel.offers.modal.yourCouponLink") }}
                </p>
                <ButtonV2
                  v-if="couponValue"
                  variant="primary"
                  size="lg"
                  class="w-full !h-[50px] !text-[17px] !font-semibold !rounded-full"
                  @click="onGoToOffer"
                >
                  <Icon icon="mdi:open-in-new" class="text-base" />
                  {{ $t("channel.offers.modal.goToOffer") }}
                </ButtonV2>
                <p
                  v-if="couponValue"
                  class="text-sm text-text-secondary truncate"
                >
                  {{ couponValue }}
                </p>
                <div
                  class="flex items-center gap-2 rounded-lg bg-surface-secondary px-3 py-2"
                >
                  <Icon
                    icon="mdi:information-outline"
                    class="text-icon-secondary text-base shrink-0"
                  />
                  <span class="text-sm text-text-secondary">
                    {{ $t("channel.offers.modal.singleUseNote") }}
                  </span>
                </div>
              </template>

              <!-- Unknown type fallback -->
              <template v-else>
                <p
                  v-if="couponValue"
                  class="text-2xl text-text-primary font-mono bg-surface-secondary border border-border-brand rounded-2xl px-4 py-3 select-all break-all"
                >
                  {{ couponValue }}
                </p>
                <div
                  class="flex items-center gap-2 rounded-lg bg-surface-secondary px-3 py-2"
                >
                  <Icon
                    icon="mdi:information-outline"
                    class="text-icon-secondary text-base shrink-0"
                  />
                  <span class="text-sm text-text-secondary">
                    {{ $t("channel.offers.modal.singleUseNote") }}
                  </span>
                </div>
              </template>
            </div>

            <!-- Claim button section -->
            <div v-else class="flex flex-col gap-3">
              <ButtonV2
                variant="primary"
                size="lg"
                class="w-full !h-[50px] !text-[17px] !font-semibold !rounded-full"
                :disabled="isSoldOut || claiming"
                :is-loading="claiming"
                @click="onClaimOffer"
              >
                {{ $t("channel.offers.modal.claimOffer") }}
              </ButtonV2>
            </div>
          </div>
        </ModalBodyV2>
      </template>
    </ModalRoot>
  </Teleport>
</template>

<script lang="ts">
import { graphql as gql } from "@/generated/gql";
import type { ResultOf } from "@graphql-typed-document-node/core";

export const OfferDetailModalFragment = gql(/* GraphQL */ `
  fragment OfferDetailModalData on Offer {
    id
    name
    description
    short_description
    image_url
    url
    offer_coupon_type
    valid_from
    valid_until
    user_has_claimed
    user_claimed_coupon {
      id
      offer_coupon {
        id
        decrypted_value
      }
    }
    authorization_rule_group {
      id
      ...AuthorizationRuleGroupForGating
    }
    poolStats {
      available
    }
  }
`);

export type OfferDetailModalData = ResultOf<typeof OfferDetailModalFragment>;

const ClaimOfferMutationDocument = gql(/* GraphQL */ `
  mutation ClaimOfferFromModal($offer_id: String!) {
    claimOffer(offer_id: $offer_id) {
      __typename
      ... on OfferCouponClaim {
        id
        offer_coupon {
          id
          decrypted_value
        }
      }
      ... on OfferAlreadyClaimedError {
        error
      }
      ... on OfferNoCouponsAvailableError {
        error
      }
      ... on OfferNotYetActiveError {
        error
      }
      ... on OfferExpiredError {
        error
      }
      ... on OfferNotActiveError {
        error
      }
      ... on OfferNotFoundError {
        error
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);
</script>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useMutation } from "@urql/vue";
import { Icon } from "@iconify/vue/dist/iconify.js";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ModalBodyV2 from "@/components/ui/modal/modal-body-v2.vue";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { portalRootElementSelector } from "@/constants/ui";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import { useProvideGatingPermissions } from "@/features/gating/use-gating-permissions";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";
import { ChannelPermission } from "@f3/shared/permissions";
import { ExperienceAction } from "@/enums/experience-type.enum";
import { ExperienceType, OfferCouponType } from "@/generated/graphql";
import { useToastStore } from "@/stores/toast.store";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useCopyToClipboard } from "@/composables/use-share-or-copy";

const props = defineProps<{
  offer: OfferDetailModalData | null;
}>();

const emit = defineEmits<{
  claimed: [];
}>();

const isOpen = defineModel<boolean>({ required: true });

const { t } = useI18n();
const toastStore = useToastStore();
const trackingStore = useTrackingStore();
const copyToClipboard = useCopyToClipboard();

// ---------------------------------------------------------
// Modal queue coordination
// ---------------------------------------------------------
useJustOneOpenModal(isOpen);

// ---------------------------------------------------------
// Gating: provide permissions BEFORE useGatingForAction
// ---------------------------------------------------------
useProvideGatingPermissions(
  computed(() => props.offer?.authorization_rule_group),
);

// ---------------------------------------------------------
// State
// ---------------------------------------------------------
const claiming = ref(false);
const claimedCoupon = ref<{
  id: string;
  offer_coupon: { id: string; decrypted_value?: string | null };
} | null>(null);

const isSoldOut = computed(
  () =>
    props.offer?.poolStats != null &&
    props.offer.poolStats.available === 0 &&
    !props.offer.user_has_claimed,
);

const couponValue = computed(() => {
  if (claimedCoupon.value) {
    return claimedCoupon.value.offer_coupon.decrypted_value ?? null;
  }
  return null;
});

// ---------------------------------------------------------
// GraphQL mutation
// ---------------------------------------------------------
const { executeMutation } = useMutation(ClaimOfferMutationDocument);

// ---------------------------------------------------------
// Gating experience (computed ref for reactivity)
// ---------------------------------------------------------
const gatingExperience = computed(() => ({
  experienceId: props.offer?.id,
  experienceTitle: props.offer?.name,
  experienceType: ExperienceType.Offer,
  experienceAction: ExperienceAction.CLAIM_OFFER,
}));

// ---------------------------------------------------------
// Claim action
// ---------------------------------------------------------
async function executeClaimAction() {
  if (!props.offer) return;
  claiming.value = true;
  try {
    const result = await executeMutation({ offer_id: props.offer.id });
    const data = result.data?.claimOffer;
    if (!data) {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.error"),
        type: "error",
      });
      return;
    }

    if (data.__typename === "OfferCouponClaim") {
      claimedCoupon.value = {
        id: data.id,
        offer_coupon: {
          id: data.offer_coupon.id,
          decrypted_value: data.offer_coupon.decrypted_value,
        },
      };
      toastStore.addToast({
        message: t("channel.offers.modal.claim.success"),
        type: "success",
      });
      trackingStore.track({
        event: TrackEventEnum.OfferClaimed,
        category: EventCategory.Engagement,
        metadata: {
          offerId: props.offer.id,
          offerDescription: props.offer.description,
          experienceId: props.offer.id,
          experienceTitle: props.offer.name,
        },
      });
      emit("claimed");
    } else if (data.__typename === "OfferAlreadyClaimedError") {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.alreadyClaimed"),
        type: "error",
      });
    } else if (data.__typename === "OfferNoCouponsAvailableError") {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.soldOut"),
        type: "error",
      });
    } else if (data.__typename === "OfferNotYetActiveError") {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.notYetActive"),
        type: "error",
      });
    } else if (data.__typename === "OfferExpiredError") {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.expired"),
        type: "error",
      });
    } else if (data.__typename === "OfferNotActiveError") {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.notActive"),
        type: "error",
      });
    } else if (data.__typename === "UnauthenticatedError") {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.unauthenticated"),
        type: "error",
      });
    } else {
      toastStore.addToast({
        message: t("channel.offers.modal.claim.error"),
        type: "error",
      });
    }
  } finally {
    claiming.value = false;
  }
}

const { handler: onClaimOffer } = useGatingForAction(
  ChannelPermission.claimOffers,
  executeClaimAction,
  { gatingExperience },
);

// ---------------------------------------------------------
// Actions
// ---------------------------------------------------------
function onCopyCode() {
  if (couponValue.value) {
    copyToClipboard(couponValue.value);
  }
}

function onGoToOffer() {
  if (couponValue.value) {
    window.open(couponValue.value, "_blank", "noopener,noreferrer");
  }
}

function onClose() {
  isOpen.value = false;
}

// ---------------------------------------------------------
// Amplitude: track modal open
// ---------------------------------------------------------
// Track which offer the claimedCoupon belongs to, so we only reset when
// opening a different offer (not on re-entry to the same one).
const lastClaimedOfferId = ref<string | null>(null);

watch(isOpen, (open) => {
  if (open && props.offer) {
    // Only reset if opening a different offer than the one we claimed
    if (lastClaimedOfferId.value !== props.offer.id) {
      claimedCoupon.value = null;
    }
    // Populate coupon from server if user has previously claimed
    if (!claimedCoupon.value && props.offer.user_claimed_coupon) {
      claimedCoupon.value = {
        id: props.offer.user_claimed_coupon.id,
        offer_coupon: {
          id: props.offer.user_claimed_coupon.offer_coupon.id,
          decrypted_value:
            props.offer.user_claimed_coupon.offer_coupon.decrypted_value,
        },
      };
    }
    trackingStore.track({
      event: TrackEventEnum.OfferWatched,
      category: EventCategory.Engagement,
    });
  }
});

// Keep lastClaimedOfferId in sync with successful claims
watch(claimedCoupon, (claim) => {
  if (claim && props.offer) {
    lastClaimedOfferId.value = props.offer.id;
  }
});

// ---------------------------------------------------------
// Helpers
// ---------------------------------------------------------
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
</script>
